// src/api/opportunitiesApi.ts

import { baseApi } from './baseApi';
import type {
  Opportunity,
  CreateOpportunityRequest,
  UpdateOpportunityRequest,
  UpdateOpportunityStageRequest,
} from '../types/api';

interface GetOpportunitiesParams {
  workspaceId?: string;
  pipelineId?: string;
  status?: string;
  q?: string;
}

export const opportunitiesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOpportunities: builder.query<Opportunity[], GetOpportunitiesParams>({
      query: (params) => ({
        url: '/opportunities',
        params: {
          ...(params.workspaceId && { workspaceId: params.workspaceId }),
          ...(params.pipelineId && { pipelineId: params.pipelineId }),
          ...(params.status && { status: params.status }),
          ...(params.q && { q: params.q }),
        },
      }),
      providesTags: (result, error, { workspaceId, pipelineId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Opportunity' as const, id })),
              { type: 'Opportunity', id: pipelineId || workspaceId || 'LIST' },
            ]
          : [{ type: 'Opportunity', id: pipelineId || workspaceId || 'LIST' }],
    }),

    getOpportunity: builder.query<Opportunity, string>({
      query: (id) => `/opportunities/${id}`,
      providesTags: (result, error, id) => [{ type: 'Opportunity', id }],
    }),

    createOpportunity: builder.mutation<Opportunity, CreateOpportunityRequest>({
      query: (body) => ({
        url: '/opportunities',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { workspaceId, pipelineId }) => [
        { type: 'Opportunity', id: pipelineId || workspaceId || 'LIST' },
      ],
    }),

    updateOpportunity: builder.mutation<Opportunity, { id: string } & UpdateOpportunityRequest>({
      query: ({ id, ...body }) => ({
        url: `/opportunities/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Opportunity', id },
        { type: 'Opportunity', id: 'LIST' },
      ],
    }),

    updateOpportunityStage: builder.mutation<Opportunity, { id: string } & UpdateOpportunityStageRequest>({
      query: ({ id, ...body }) => ({
        url: `/opportunities/${id}/stage`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Opportunity', id },
        { type: 'Opportunity', id: 'LIST' },
      ],
    }),

    deleteOpportunity: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/opportunities/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Opportunity', id },
        { type: 'Opportunity', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetOpportunitiesQuery,
  useGetOpportunityQuery,
  useCreateOpportunityMutation,
  useUpdateOpportunityMutation,
  useUpdateOpportunityStageMutation,
  useDeleteOpportunityMutation,
} = opportunitiesApi;
